import { useEffect, useState } from 'react'
import { orgApi } from '@carrent/api'
import { PersonAvatar } from './OrgTree'
import { UserPicker } from '../../tasks/UserPicker'

/**
 * Замещения: активные и запланированные сверху, история ниже.
 * Назначает только владелец — решение постановки; бэкенд это
 * дополнительно закрепляет RBAC-ом.
 */

const STATUS_LABEL = {
  active:    'Действует',
  scheduled: 'Запланировано',
  expired:   'Завершено',
  revoked:   'Отозвано',
}

function fmtRange(s) {
  const opt = { day: 'numeric', month: 'short', year: 'numeric' }
  const a = new Date(s.starts_at).toLocaleDateString('ru-RU', opt)
  const b = new Date(s.ends_at).toLocaleDateString('ru-RU', opt)
  return `${a} — ${b}`
}

function toLocalInput(date) {
  const p = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`
}

export function SubstitutionForm({ regions, onDone, onCancel }) {
  const [principal, setPrincipal] = useState(null)
  const [deputy, setDeputy] = useState(null)
  const [regionId, setRegionId] = useState('')
  const [startsAt, setStartsAt] = useState(() => toLocalInput(new Date()))
  const [endsAt, setEndsAt] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14); return toLocalInput(d)
  })
  const [reason, setReason] = useState('')
  const [err, setErr] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    setErr('')
    if (!principal) { setErr('Выберите, кого замещают'); return }
    if (!deputy) { setErr('Выберите заместителя'); return }
    setSaving(true)
    try {
      await orgApi.createSubstitution({
        principal_id: principal.id,
        deputy_id:    deputy.id,
        region_id:    regionId ? Number(regionId) : null,
        starts_at:    new Date(startsAt).toISOString(),
        ends_at:      new Date(endsAt).toISOString(),
        reason:       reason.trim() || null,
      })
      onDone()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось создать замещение')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="org-subform" data-testid="substitution-form">
      <div className="field">
        <label className="field__label">Кого замещают</label>
        <UserPicker selected={principal} onChange={setPrincipal}
          placeholder="Найти менеджера..." role="manager" />
      </div>
      <div className="field">
        <label className="field__label">Заместитель</label>
        <UserPicker selected={deputy} onChange={setDeputy}
          placeholder="Найти заместителя..." role="manager" />
      </div>
      <div className="field">
        <label className="field__label">Объём</label>
        <select className="input" value={regionId}
          onChange={(e) => setRegionId(e.target.value)}>
          <option value="">Все зоны ответственности</option>
          {regions.map((r) => (
            <option key={r.id} value={r.id}>Только {r.name}</option>
          ))}
        </select>
      </div>
      <div className="org-subform__dates">
        <div className="field">
          <label className="field__label">Начало</label>
          <input type="datetime-local" className="input" value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label">Окончание</label>
          <input type="datetime-local" className="input" value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label className="field__label">Причина</label>
        <input className="input" value={reason} placeholder="Отпуск, командировка..."
          onChange={(e) => setReason(e.target.value)} maxLength={300} />
      </div>

      {err && <div className="alert alert--error">{err}</div>}

      <div className="org-subform__actions">
        <button className="btn btn--primary" onClick={submit} disabled={saving}>
          {saving ? <span className="spinner" /> : 'Назначить замещение'}
        </button>
        <button className="btn" onClick={onCancel} disabled={saving}>Отмена</button>
      </div>
    </div>
  )
}

function SubstitutionRow({ sub, onRevoke }) {
  const finished = sub.status === 'expired' || sub.status === 'revoked'
  return (
    <div className={`org-sub ${finished ? 'org-sub--done' : ''}`}>
      <span className={`org-sub__chip org-sub__chip--${sub.status}`}>
        {STATUS_LABEL[sub.status] || sub.status}
      </span>
      <div className="org-sub__who">
        <PersonAvatar name={sub.deputy?.full_name} size="sm" />
        <div>
          <div className="org-sub__deputy">{sub.deputy?.full_name}</div>
          <div className="org-sub__meta">
            за {sub.principal?.full_name}
            {' · '}{sub.region ? sub.region.name : 'все зоны'}
            {' · '}{fmtRange(sub)}
            {sub.reason ? ` · ${sub.reason}` : ''}
          </div>
        </div>
      </div>
      {!finished && (
        <button className="btn btn--sm" onClick={() => onRevoke(sub)}>Отозвать</button>
      )}
    </div>
  )
}

export default function SubstitutionsTab({ regions, onChanged }) {
  const [subs, setSubs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [err, setErr] = useState('')

  const load = () => {
    setLoading(true)
    orgApi.substitutions()
      .then((r) => setSubs(r.data?.data || []))
      .catch(() => setErr('Не удалось загрузить замещения'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const revoke = async (sub) => {
    if (!confirm(`Отозвать замещение (${sub.deputy?.full_name})?`)) return
    try {
      await orgApi.revokeSubstitution(sub.id)
      load(); onChanged?.()
    } catch (e) {
      alert(e.response?.data?.message || 'Ошибка')
    }
  }

  const current = subs.filter((s) => s.status === 'active' || s.status === 'scheduled')
  const history = subs.filter((s) => s.status === 'expired' || s.status === 'revoked')

  return (
    <div className="flex-col org-page__body">
      {!showForm && (
        <div>
          <button className="btn btn--primary" onClick={() => setShowForm(true)}>
            + Назначить замещение
          </button>
        </div>
      )}
      {showForm && (
        <SubstitutionForm
          regions={regions}
          onDone={() => { setShowForm(false); load(); onChanged?.() }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading && <div className="spinner" />}
      {err && !loading && <div className="alert alert--error">{err}</div>}

      {!loading && current.length === 0 && !showForm && (
        <p className="text-muted">
          Активных замещений нет — согласования идут ответственным за регионы.
        </p>
      )}
      {current.map((s) => <SubstitutionRow key={s.id} sub={s} onRevoke={revoke} />)}

      {history.length > 0 && (
        <>
          <h3 className="org-h3">История</h3>
          {history.map((s) => <SubstitutionRow key={s.id} sub={s} onRevoke={revoke} />)}
        </>
      )}
    </div>
  )
}
